import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Stack, Avatar, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Chip, IconButton, Collapse, Divider,
} from '@mui/material';
import {
  TrendingUp, AccountBalance, Business, ExpandMore, ExpandLess,
  Receipt, Refresh, AttachMoney, BarChart,
} from '@mui/icons-material';
import { paymentsAPI } from '../../api/client';

function RevenueAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchRevenue = async () => {
    setLoading(true);
    try {
      const res = await paymentsAPI.revenueByProperty();
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch revenue data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRevenue(); }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box textAlign="center" py={8}>
        <Typography color="text.secondary">Failed to load revenue data.</Typography>
      </Box>
    );
  }

  const summaryCards = [
    { label: 'Total Revenue', value: `GH\u20B5 ${data.total_revenue?.toLocaleString()}`, color: '#10b981', icon: <TrendingUp /> },
    { label: 'Platform Commission', value: `GH\u20B5 ${data.total_commission?.toLocaleString()}`, color: '#f59e0b', icon: <AccountBalance /> },
    { label: 'Your Net Earnings', value: `GH\u20B5 ${data.total_net?.toLocaleString()}`, color: '#6366f1', icon: <AttachMoney /> },
    { label: 'Properties with Revenue', value: data.properties?.filter(p => p.payment_count > 0).length || 0, color: '#3b82f6', icon: <Business /> },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #10b981, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Revenue Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track revenue per property based on confirmed payments
            {data.commission_rate ? ` (Commission: ${data.commission_rate}%)` : ''}
          </Typography>
        </Box>
        <IconButton onClick={fetchRevenue} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {summaryCards.map((item, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ borderLeft: `4px solid ${item.color}`, height: '100%' }}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 42, height: 42 }}>{item.icon}</Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{item.label}</Typography>
                    <Typography variant="h5" fontWeight={800}>{item.value}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Per-Property Revenue Table */}
      {data.properties?.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <BarChart sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" fontWeight={600}>No revenue data yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Revenue will appear here once tenants make confirmed payments for your properties.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell width={40} />
                <TableCell><strong>Property</strong></TableCell>
                <TableCell align="right"><strong>Total Revenue</strong></TableCell>
                <TableCell align="right"><strong>Commission</strong></TableCell>
                <TableCell align="right"><strong>Net Earnings</strong></TableCell>
                <TableCell align="center"><strong>Payments</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.properties.map((prop) => (
                <React.Fragment key={prop.property_id}>
                  <TableRow
                    hover
                    sx={{ cursor: prop.payment_count > 0 ? 'pointer' : 'default', '& > *': { borderBottom: expandedRow === prop.property_id ? 0 : undefined } }}
                    onClick={() => prop.payment_count > 0 && setExpandedRow(expandedRow === prop.property_id ? null : prop.property_id)}
                  >
                    <TableCell>
                      {prop.payment_count > 0 && (
                        <IconButton size="small">
                          {expandedRow === prop.property_id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2">{prop.property_title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {prop.property_address}{prop.property_city ? `, ${prop.property_city}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color={prop.total_revenue > 0 ? 'success.main' : 'text.secondary'}>
                        GH&#x20B5; {prop.total_revenue?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        GH&#x20B5; {prop.commission?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color="primary">
                        GH&#x20B5; {prop.net_revenue?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={prop.payment_count}
                        size="small"
                        color={prop.payment_count > 0 ? 'success' : 'default'}
                        variant={prop.payment_count > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>
                  {/* Expanded Payment Details */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0 }}>
                      <Collapse in={expandedRow === prop.property_id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                            Payment History
                          </Typography>
                          <Divider sx={{ mb: 1.5 }} />
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Transaction Ref</strong></TableCell>
                                <TableCell align="right"><strong>Amount</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Date</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {prop.payments?.map((pmt) => (
                                <TableRow key={pmt.id}>
                                  <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      <Receipt sx={{ fontSize: 14, color: 'text.secondary' }} />
                                      <Typography variant="body2">{pmt.transaction_ref}</Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontWeight={600}>
                                      GH&#x20B5; {pmt.amount?.toLocaleString()}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={pmt.status} size="small" color="success" sx={{ textTransform: 'capitalize' }} />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {pmt.paid_at ? new Date(pmt.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default RevenueAnalyticsPage;
